import { Injectable, inject } from '@angular/core';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationService, NotificationServiceInjector } from '@next/core-lib/notification';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { map, filter, concatMap, withLatestFrom } from 'rxjs/operators';

import { SiteState } from 'src/app/shared/models';

import * as CopyCropFieldsToManureSpaceActions from './copy-cropfields-to-manure-space.actions';
import * as CopyCropFieldsToManureSpaceSelectors from './copy-cropfields-to-manure-space.selectors';
// eslint-disable-next-line max-len
import { CopyCropFieldsToManureSpaceProgressDialogComponent } from '../components/copy-cropfields-to-manure-space-progress-dialog/copy-cropfields-to-manure-space-progress-dialog.component';
import {
  CopyCropFieldsToManureSpaceStarted,
  CopyCropFieldsToManureSpaceCompleted,
  CopyCropFieldsToManureSpaceEvents,
} from '../copy-cropfields-to-manure-space.models';

@Injectable()
export class CopyCropFieldsToManureSpaceEffects {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly notification = inject<NotificationService>(NotificationServiceInjector);

  copyCropFieldsToManureSpaceStartedEvent$ = createEffect(() =>
    this.notification
      .on<CopyCropFieldsToManureSpaceStarted>(
        CopyCropFieldsToManureSpaceEvents.CopyCropFieldsToManureSpaceStarted,
      )
      .pipe(
        map(({ amountOfCropFields }) =>
          CopyCropFieldsToManureSpaceActions.copyCropFieldsToManureSpaceStarted({
            amountOfCropFields,
          }),
        ),
      ),
  );

  cropFieldsToManureSpaceCompletedEvent$ = createEffect(() =>
    this.notification
      .on<CopyCropFieldsToManureSpaceCompleted>(
        CopyCropFieldsToManureSpaceEvents.CopyCropFieldsToManureSpaceCompleted,
      )
      .pipe(
        map(({ isSuccessful, message }) =>
          CopyCropFieldsToManureSpaceActions.copyCropFieldsToManureSpaceCompleted({
            isSuccessful,
            message,
          }),
        ),
      ),
  );

  copyCropFieldsToManureSpaceStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyCropFieldsToManureSpaceActions.copyCropFieldsToManureSpaceStarted),
      concatMap((_) =>
        this.dialog.dialog(
          CopyCropFieldsToManureSpaceProgressDialogComponent,
          { title: this.locale.translate('copyCropFieldsToManureSpace.title') },
          { autoFocus: false },
        ),
      ),
      withLatestFrom(this.store.pipe(select(CopyCropFieldsToManureSpaceSelectors.isCopying))),
      filter(([_, isCopying]) => isCopying),
      map((_) => CopyCropFieldsToManureSpaceActions.copyDialogDismissed()),
    ),
  );

  copyCropFieldsToManureSpaceCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CopyCropFieldsToManureSpaceActions.copyCropFieldsToManureSpaceCompleted),
      withLatestFrom(this.store.pipe(select(CopyCropFieldsToManureSpaceSelectors.isCopying))),
      filter(([_, isCopying]) => !isCopying),
      map((_) => CopyCropFieldsToManureSpaceActions.completedCopyAll()),
    ),
  );
}
